import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Anchor, Plus, X, ExternalLink, Globe, Building2, ShieldCheck, Activity, ChevronRight } from "lucide-react";

const fmtUSD = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n || 0));

const STATUS_TONE = {
  provisioning: { color: "#FBBF24", label: "PROVISIONING" },
  active: { color: "#34D399", label: "ACTIVE" },
  suspended: { color: "#F87171", label: "SUSPENDED" },
  archived: { color: "#71717A", label: "ARCHIVED" },
};

export default function B2BProvisioningPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    owner_email: "",
    owner_full_name: "",
    primary_color: "#0F172A",
    accent_color: "#22D3EE",
  });
  const [provisionResult, setProvisionResult] = useState(null);

  const load = async () => {
    try {
      const res = await api.get("/admin/shipyards");
      setData(res.data);
    } catch (e) {
      const detail = e && e.response && e.response.data && e.response.data.detail;
      setErr(detail || (e && e.message) || "Unknown error");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setProvisionResult(null);
    try {
      const res = await api.post("/admin/shipyards", {
        name: form.name,
        owner_email: form.owner_email,
        owner_full_name: form.owner_full_name || null,
        branding: {
          primary_color: form.primary_color,
          accent_color: form.accent_color,
        },
      });
      setProvisionResult(res.data);
      setForm({ name: "", owner_email: "", owner_full_name: "", primary_color: "#0F172A", accent_color: "#22D3EE" });
      await load();
    } catch (e) {
      const detail = e && e.response && e.response.data && e.response.data.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ") : String(detail || e.message);
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const startCheckout = async (shipyardId) => {
    try {
      const res = await api.post(`/admin/shipyards/${shipyardId}/start-checkout`);
      if (res.data.checkout_url) {
        window.open(res.data.checkout_url, "_blank");
      } else {
        alert(
          "Bridge fallback: " +
            (res.data.instructions || "Stripe price ID not configured. Invoice manually.")
        );
      }
    } catch (e) {
      const detail = e && e.response && e.response.data && e.response.data.detail;
      alert("Checkout error: " + (detail || e.message));
    }
  };

  if (err && !data) {
    return (
      <div data-testid="b2b-panel-error" className="glass-strong rounded-xl p-6 border border-red-500/30 text-sm text-red-300 mb-8">
        Unable to load Child Shipyards · {err}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="glass-strong rounded-xl p-8 border border-white/10 text-center text-xs text-zinc-500 tracking-armada mb-8">
        SCANNING B2B FLEET…
      </div>
    );
  }

  const totals = data.totals || {};

  return (
    <div data-testid="b2b-panel" className="glass-strong rounded-2xl p-6 lg:p-8 border border-cyan-400/20 mb-8 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 90% 10%, rgba(34,211,238,0.45), transparent 60%)," +
            "radial-gradient(ellipse at 5% 90%, rgba(212,175,55,0.20), transparent 60%)",
        }}
      />
      <div className="relative">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Anchor className="w-4 h-4 text-cyan-300" />
              <div className="text-[10px] tracking-armada text-cyan-300 font-bold">
                SOVEREIGN COMMANDER · B2B PROVISIONING
              </div>
              <span className="ml-2 text-[10px] tracking-wider text-cyan-200 px-2 py-0.5 rounded-md border border-cyan-400/40 bg-cyan-400/10">
                ${data.subscription_tier_price_usd}/mo · {data.infrastructure_tax_pct}% tax
              </span>
            </div>
            <h3 className="text-2xl md:text-3xl font-black tracking-tighter text-white">
              Child Shipyards · Multi-Fleet Hub
            </h3>
            <p className="text-xs text-zinc-500 mt-1">
              Provision white-labeled fleets for external customers. Each shipyard pays $499/mo and remits a 1% Infrastructure Tax on every signal.
            </p>
          </div>
          <button
            onClick={() => { setShowForm((s) => !s); setProvisionResult(null); }}
            data-testid="b2b-toggle-form"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-cyan-400/15 hover:bg-cyan-400/25 border border-cyan-400/50 text-cyan-200 text-xs font-bold tracking-wider uppercase transition"
          >
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "Cancel" : "Provision Child Shipyard"}
          </button>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
          <div className="px-4 py-3 rounded-lg border border-cyan-400/20 bg-black/30">
            <div className="text-[10px] tracking-wider text-cyan-300/80">FLEETS</div>
            <div className="text-2xl font-black text-white tabular-nums">{totals.count || 0}</div>
          </div>
          <div className="px-4 py-3 rounded-lg border border-cyan-400/20 bg-black/30">
            <div className="text-[10px] tracking-wider text-cyan-300/80">REVENUE SIGNALED · LIFETIME</div>
            <div className="text-2xl font-black text-white tabular-nums">{fmtUSD(totals.revenue_signaled_usd)}</div>
          </div>
          <div className="px-4 py-3 rounded-lg border border-cyan-400/20 bg-black/30">
            <div className="text-[10px] tracking-wider text-cyan-300/80">TAX COLLECTED · LIFETIME</div>
            <div className="text-2xl font-black text-white tabular-nums">{fmtUSD(totals.tax_collected_usd)}</div>
          </div>
          <div className="px-4 py-3 rounded-lg border border-cyan-400/20 bg-black/30">
            <div className="text-[10px] tracking-wider text-cyan-300/80">MRR · IF ALL ACTIVE</div>
            <div className="text-2xl font-black text-white tabular-nums">
              {fmtUSD((totals.count || 0) * data.subscription_tier_price_usd)}
            </div>
          </div>
        </div>

        {/* Provisioning form */}
        {showForm && (
          <form
            onSubmit={submit}
            data-testid="b2b-provision-form"
            className="mb-6 p-5 rounded-lg border border-cyan-400/30 bg-black/40 space-y-3"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block">
                <div className="text-[10px] tracking-armada text-cyan-300 font-bold mb-1">FLEET NAME</div>
                <input
                  required
                  data-testid="b2b-input-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Acme Maritime Fleet"
                  className="w-full px-3 py-2 rounded bg-black/60 border border-white/10 text-white text-sm focus:border-cyan-400/60 focus:outline-none"
                />
              </label>
              <label className="block">
                <div className="text-[10px] tracking-armada text-cyan-300 font-bold mb-1">OWNER EMAIL</div>
                <input
                  required
                  data-testid="b2b-input-email"
                  type="email"
                  value={form.owner_email}
                  onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                  placeholder="ceo@acme.com"
                  className="w-full px-3 py-2 rounded bg-black/60 border border-white/10 text-white text-sm focus:border-cyan-400/60 focus:outline-none"
                />
              </label>
              <label className="block">
                <div className="text-[10px] tracking-armada text-cyan-300 font-bold mb-1">OWNER FULL NAME (OPTIONAL)</div>
                <input
                  data-testid="b2b-input-fullname"
                  type="text"
                  value={form.owner_full_name}
                  onChange={(e) => setForm({ ...form, owner_full_name: e.target.value })}
                  placeholder="Captain Acme"
                  className="w-full px-3 py-2 rounded bg-black/60 border border-white/10 text-white text-sm focus:border-cyan-400/60 focus:outline-none"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <div className="text-[10px] tracking-armada text-cyan-300 font-bold mb-1">PRIMARY</div>
                  <input
                    data-testid="b2b-input-primary"
                    type="color"
                    value={form.primary_color}
                    onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                    className="w-full h-9 rounded bg-black/60 border border-white/10 cursor-pointer"
                  />
                </label>
                <label className="block">
                  <div className="text-[10px] tracking-armada text-cyan-300 font-bold mb-1">ACCENT</div>
                  <input
                    data-testid="b2b-input-accent"
                    type="color"
                    value={form.accent_color}
                    onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                    className="w-full h-9 rounded bg-black/60 border border-white/10 cursor-pointer"
                  />
                </label>
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="b2b-submit"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-gradient-to-r from-cyan-400 to-cyan-500 text-black text-xs font-bold tracking-wider uppercase hover:from-cyan-300 hover:to-cyan-400 transition disabled:opacity-50"
            >
              <ShieldCheck className="w-4 h-4" />
              {submitting ? "Provisioning…" : "Commission Shipyard"}
            </button>
          </form>
        )}

        {provisionResult && (
          <div
            data-testid="b2b-provision-result"
            className="mb-5 p-4 rounded-lg border border-emerald-400/40 bg-emerald-400/5"
          >
            <div className="text-[10px] tracking-armada text-emerald-300 font-bold mb-1">
              ✓ COMMISSIONED · {provisionResult.name}
            </div>
            <div className="text-xs text-zinc-300 leading-relaxed">
              Slug · <code className="text-emerald-200">{provisionResult.slug}</code> ·
              Status · <code className="text-emerald-200">{provisionResult.status}</code>
              {provisionResult.owner_temp_password && (
                <>
                  {" · "}
                  Owner temp password ·{" "}
                  <code className="text-amber-200 select-all">{provisionResult.owner_temp_password}</code>
                </>
              )}
            </div>
          </div>
        )}

        {/* Shipyard list */}
        <div className="space-y-2">
          {(data.shipyards || []).length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center border border-dashed border-white/10 rounded-md">
              No Child Shipyards yet. Commission your first to begin generating $499/mo + 1% tax revenue.
            </div>
          ) : (
            (data.shipyards || []).map((sy) => {
              const tone = STATUS_TONE[sy.status] || STATUS_TONE.archived;
              const metrics = sy.metrics || {};
              return (
                <div
                  key={sy.id}
                  data-testid={"b2b-shipyard-" + sy.id}
                  className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-lg border border-white/5 bg-black/30 hover:border-cyan-400/30 transition"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border"
                      style={{
                        background: (sy.branding && sy.branding.primary_color) || "#0F172A",
                        borderColor: (sy.branding && sy.branding.accent_color) || "#22D3EE",
                      }}
                    >
                      <Building2
                        className="w-5 h-5"
                        style={{ color: (sy.branding && sy.branding.accent_color) || "#22D3EE" }}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-bold text-white truncate">{sy.name}</span>
                        <span
                          className="text-[9px] tracking-armada font-bold px-1.5 py-0.5 rounded uppercase"
                          style={{ color: tone.color, borderWidth: 1, borderStyle: "solid", borderColor: tone.color + "40", background: tone.color + "10" }}
                        >
                          {tone.label}
                        </span>
                      </div>
                      <div className="text-[10px] text-zinc-500 flex items-center gap-3 mt-0.5">
                        <span className="flex items-center gap-1">
                          <Globe className="w-3 h-3" /> /{sy.slug}
                        </span>
                        <span>· {sy.owner_email}</span>
                        <span className="flex items-center gap-1">
                          <Activity className="w-3 h-3" /> {metrics.transaction_count || 0} signals
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right hidden sm:block">
                      <div className="text-[9px] tracking-wider text-zinc-500">TAX COLLECTED</div>
                      <div className="text-sm font-bold text-cyan-200 tabular-nums">
                        {fmtUSD((metrics.infrastructure_tax_total_cents || 0) / 100)}
                      </div>
                    </div>
                    <button
                      onClick={() => startCheckout(sy.id)}
                      data-testid={"b2b-checkout-" + sy.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[10px] tracking-armada font-bold border border-amber-400/40 bg-amber-400/10 text-amber-200 hover:bg-amber-400/20 transition"
                      title={`Send $${data.subscription_tier_price_usd}/mo checkout`}
                    >
                      <ExternalLink className="w-3 h-3" />
                      CHECKOUT
                    </button>
                    <ChevronRight className="w-4 h-4 text-zinc-600" />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
